import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Package, Barcode, ExternalLink, CheckCircle, Clock, AlertTriangle, Truck, X, Star, Globe, ShoppingCart, Upload, Image, Loader2, Filter, Download, ChevronDown, ChevronUp, PowerOff, Power, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import HighlightText from '@/components/HighlightText';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { productsAPI, categoriesAPI, brandsAPI, uomAPI, taxAPI, vendorsAPI, assetsAPI } from '@/services/api';
import api from '@/services/api';
import { WeightHint } from '@/components/hq/WeightHint';

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const IMAGE_MAX_BYTES = 3 * 1024 * 1024;


const initialFormData = {
  sku: '',
  name: '',
  description: '',
  brand_id: '',
  category_id: '',
  hsn_code: '',
  uom_id: '',
  tax_group_id: '',
  supplier_ids: [],
  primary_supplier_id: '',
  low_stock_alert_qty: 0,
  barcodes: [{ barcode: '', is_default: true, variant_id: null }],
  // UOM Conversion for Purchase vs Sales
  base_uom: '',  // Sales/Stock unit (e.g., "PCS")
  purchase_uom: '',  // Purchase unit (e.g., "CTN", "BAG")
  uom_conversion_factor: 1,  // 1 purchase_uom = X base_uom
  // E-commerce settings
  ecom_enabled: false,
  online_stock_percent: 100,
  // Local purchase setting
  allow_local_purchase: false,
  // Shipping dimensions
  weight_grams: 0,
  length_cm: 0,
  width_cm: 0,
  height_cm: 0
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [brands, setBrands] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [totalMatches, setTotalMatches] = useState(0);
  const searchReqRef = useRef(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [selectedSupplierToAdd, setSelectedSupplierToAdd] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [storageConfigured, setStorageConfigured] = useState(false);
  // Category selection state for hierarchical dropdown
  const [selectedCategories, setSelectedCategories] = useState([null, null, null]); // Level 1, 2, 3
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  const { toast } = useToast();
  
  // NEW: Filter state
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncProgress, setResyncProgress] = useState(null); // {processed,total,updated}

  // Batch-by-batch re-sync (50 products per call) so big catalogs never hit a proxy timeout.
  const runResync = async () => {
    setResyncing(true); setResyncProgress({ processed: 0, total: 0, updated: 0 });
    let skip = 0, updated = 0, total = 0, done = false, last = null;
    try {
      while (!done) {
        const r = await api.post('/products/resync-prices', null, { params: { skip, limit: 50 } });
        last = r.data; total = r.data.total; updated += r.data.updated; skip = r.data.processed; done = r.data.done;
        setResyncProgress({ processed: r.data.processed, total, updated });
      }
      const stores = last?.synced_stores || [];
      toast({ title: `Prices re-synced · ${updated} of ${total} products updated`,
        description: stores.length ? `Synced with ${stores.length} store${stores.length > 1 ? 's' : ''}: ${stores.map(s => s.code || s.name).join(', ')} — POS prices are live now.` : 'POS prices are live now.' });
      fetchProducts();
    } catch (e) {
      toast({ title: 'Re-sync failed', description: `${e.response?.data?.detail || e.message} (stopped at ${skip}/${total || '?'})`, variant: 'destructive' });
    } finally { setResyncing(false); setTimeout(() => setResyncProgress(null), 4000); }
  };

  const fetchProducts = useCallback(async () => {
    try {
      const params = { page, page_size: 20 };
      if (search) params.search = search;
      if (filterBrand) params.brand_id = filterBrand;
      if (filterSubCategory) {
        params.category_id = filterSubCategory;
      } else if (filterCategory) {
        params.category_id = filterCategory;
      }
      if (filterSupplier) params.supplier_id = filterSupplier;
      
      const reqId = ++searchReqRef.current;
      const response = await productsAPI.list(params);
      // Ignore out-of-order responses so fast typing never shows stale results
      if (reqId !== searchReqRef.current) return;
      setProducts(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalMatches(response.data.total ?? response.data.items.length);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch products', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filterBrand, filterCategory, filterSubCategory, filterSupplier, toast]);

  // Debounce the typed text so every keystroke does not fire a request
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchMasters = useCallback(async () => {
    try {
      const [catRes, catTreeRes, brandRes, uomRes, taxRes, vendorRes] = await Promise.all([
        categoriesAPI.list({ is_active: true }),
        categoriesAPI.tree(),
        brandsAPI.list({ is_active: true }),
        uomAPI.list({ is_active: true }),
        taxAPI.list({ is_active: true }),
        vendorsAPI.list({ is_active: true })
      ]);
      setCategories(catRes.data || []);
      setCategoryTree(catTreeRes.data || []);
      setBrands(brandRes.data || []);
      setUoms(uomRes.data || []);
      setTaxGroups(taxRes.data || []);
      setVendors(vendorRes.data || []);
      
      // Check if asset storage is configured
      try {
        const storageRes = await api.get('/assets/settings');
        setStorageConfigured(storageRes.data?.is_configured || false);
      } catch {
        setStorageConfigured(false);
      }
    } catch (error) {
      console.error('Error fetching masters:', error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  // Helper function to find category and its ancestors
  const findCategoryPath = (categoryId, tree) => {
    const path = [null, null, null];
    
    const findInTree = (nodes, targetId, level = 0) => {
      for (const node of nodes) {
        if (node.id === targetId) {
          path[level] = node.id;
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (findInTree(node.children, targetId, level + 1)) {
            path[level] = node.id;
            return true;
          }
        }
      }
      return false;
    };
    
    // Also search in flat categories to find parent path
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      // Build path from category's path string or parent_id
      if (category.parent_id) {
        const parent = categories.find(c => c.id === category.parent_id);
        if (parent) {
          if (parent.parent_id) {
            const grandparent = categories.find(c => c.id === parent.parent_id);
            if (grandparent) {
              return [grandparent.id, parent.id, categoryId];
            }
          }
          return [parent.id, categoryId, null];
        }
      }
      return [categoryId, null, null];
    }
    
    findInTree(tree, categoryId);
    return path;
  };

  // Get sub-categories for a given parent
  const getSubCategories = (parentId) => {
    if (!parentId) return categoryTree;
    
    const findChildren = (nodes, targetId) => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return node.children || [];
        }
        if (node.children) {
          const found = findChildren(node.children, targetId);
          if (found) return found;
        }
      }
      return [];
    };
    
    return findChildren(categoryTree, parentId);
  };

  // Handle category selection at a specific level
  const handleCategorySelect = (level, categoryId) => {
    const newSelection = [...selectedCategories];
    newSelection[level] = categoryId; // Already converted from 'none' to null in onValueChange
    // Clear lower levels when a higher level changes
    for (let i = level + 1; i < 3; i++) {
      newSelection[i] = null;
    }
    setSelectedCategories(newSelection);
    
    // Set the most specific selected category as the category_id
    const finalCategoryId = newSelection[2] || newSelection[1] || newSelection[0];
    setFormData(p => ({ ...p, category_id: finalCategoryId || '' }));
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      // Initialize category path from product's category
      const categoryPath = findCategoryPath(product.category_id, categoryTree);
      setSelectedCategories(categoryPath);
      
      setFormData({
        sku: product.sku,
        name: product.name,
        description: product.description || '',
        brand_id: product.brand_id || '',
        category_id: product.category_id || '',
        hsn_code: product.hsn_code || '',
        uom_id: product.uom_id || '',
        tax_group_id: product.tax_group_id || '',
        supplier_ids: product.supplier_ids || [],
        primary_supplier_id: product.primary_supplier_id || '',
        low_stock_alert_qty: product.low_stock_alert_qty || 0,
        barcodes: product.barcodes?.length > 0 ? product.barcodes : [{ barcode: '', is_default: true, variant_id: null }],
        // UOM Conversion
        base_uom: product.base_uom || '',
        purchase_uom: product.purchase_uom || '',
        uom_conversion_factor: product.uom_conversion_factor || 1,
        // E-commerce settings
        ecom_enabled: product.ecom_enabled || false,
        online_stock_percent: product.online_stock_percent ?? 100,
        // Local purchase setting
        allow_local_purchase: product.allow_local_purchase || false,
        // Shipping dimensions
        weight_grams: product.weight_grams || 0,
        length_cm: product.length_cm || 0,
        width_cm: product.width_cm || 0,
        height_cm: product.height_cm || 0,
        // Images
        primary_image: product.primary_image || null,
        additional_images: product.additional_images || []
      });
    } else {
      setSelectedProduct(null);
      setSelectedCategories([null, null, null]);
      setFormData({...initialFormData, primary_image: null, additional_images: []});
    }
    setDialogOpen(true);
  };

  // Image upload handlers
  const handleImageUpload = async (file, isPrimary = false) => {
    if (!selectedProduct) {
      toast({ title: 'Info', description: 'Please save the product first before uploading images', variant: 'default' });
      return;
    }
    
    if (!IMAGE_MIME_TYPES.includes((file.type || '').toLowerCase())) {
      toast({ title: 'Unsupported file', description: 'Only JPG, JPEG, PNG and WEBP images are allowed', variant: 'destructive' });
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast({ title: 'File too large', description: 'Each image must be 3MB or less', variant: 'destructive' });
      return;
    }
    
    if (!storageConfigured) {
      toast({ title: 'Error', description: 'Asset storage not configured. Please configure Bunny CDN in HQ Settings → Asset Storage.', variant: 'destructive' });
      return;
    }

    setUploadingImage(true);
    try {
      const response = await assetsAPI.uploadProductImage(selectedProduct.id, file, isPrimary);
      
      if (response.data.success) {
        // Update local state
        if (isPrimary) {
          setFormData(prev => ({ ...prev, primary_image: response.data.cdn_url }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            additional_images: [...(prev.additional_images || []), response.data.cdn_url] 
          }));
        }
        
        // Refresh product list
        fetchProducts();
        toast({ title: 'Success', description: `${isPrimary ? 'Primary' : 'Additional'} image uploaded successfully` });
      }
    } catch (error) {
      toast({ 
        title: 'Upload Failed', 
        description: error.response?.data?.detail || 'Failed to upload image', 
        variant: 'destructive' 
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageUrl, isPrimary = false) => {
    if (!selectedProduct) return;
    
    try {
      await assetsAPI.deleteProductImage(selectedProduct.id, imageUrl);
      
      if (isPrimary) {
        setFormData(prev => ({ ...prev, primary_image: null }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          additional_images: (prev.additional_images || []).filter(url => url !== imageUrl)
        }));
      }
      
      fetchProducts();
      toast({ title: 'Success', description: 'Image removed successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to remove image', 
        variant: 'destructive' 
      });
    }
  };

  const handleSave = async () => {
    if (!formData.sku || !formData.name || !formData.category_id || !formData.uom_id || !formData.tax_group_id) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    if (!formData.barcodes.some(b => b.barcode.trim())) {
      toast({ title: 'Error', description: 'At least one barcode is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        barcodes: formData.barcodes.filter(b => b.barcode.trim())
      };

      if (selectedProduct) {
        await productsAPI.update(selectedProduct.id, payload);
        toast({ title: 'Success', description: 'Product updated successfully' });
      } else {
        await productsAPI.create(payload);
        toast({ title: 'Success', description: 'Product created successfully. Configure pricing in the Pricing tab of the product.' });
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: error.response?.status === 409 ? 'Duplicate product' : 'Error',
        description: detail || 'Failed to save product',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await productsAPI.delete(selectedProduct.id);
      toast({ title: 'Deactivated', description: `"${selectedProduct.name}" was deactivated` });
      setDeleteDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to deactivate product', variant: 'destructive' });
    }
  };

  const handleReactivate = async (product) => {
    try {
      await productsAPI.reactivate(product.id);
      toast({ title: 'Reactivated', description: `"${product.name}" is now active` });
      fetchProducts();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reactivate product', variant: 'destructive' });
    }
  };

  const handleEcomToggle = async (product, next) => {
    try {
      await productsAPI.update(product.id, { ecom_enabled: next });
      toast({ title: next ? 'Ecom Enabled' : 'Ecom Disabled', description: `"${product.name}" ${next ? 'is now visible on ecom' : 'was hidden from ecom'}` });
      fetchProducts();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update ecom status', variant: 'destructive' });
    }
  };

  const addBarcode = () => {
    setFormData(p => ({ ...p, barcodes: [...p.barcodes, { barcode: '', is_default: false, variant_id: null }] }));
  };

  const updateBarcode = (index, value) => {
    setFormData(p => ({ ...p, barcodes: p.barcodes.map((b, i) => i === index ? { ...b, barcode: value } : b) }));
  };

  const removeBarcode = (index) => {
    if (formData.barcodes.length > 1) {
      setFormData(p => ({ ...p, barcodes: p.barcodes.filter((_, i) => i !== index) }));
    }
  };

  // Check if pricing is configured
  const isPricingConfigured = (product) => {
    const price = product.current_price;
    return price && price.mrp > 0 && price.selling_price > 0;
  };

  // Get default slab info
  const getDefaultSlab = (product) => {
    const price = product.current_price;
    if (!price?.price_slabs?.length) return null;
    return price.price_slabs.find(s => s.is_default) || price.price_slabs[0];
  };

  // Open pricing page with product pre-selected
  const openPricingPage = (productId) => {
    navigate(`/hq/products/${productId}/edit?tab=pricing`);
  };

  // Get subcategories for filter
  const getFilterSubCategories = (parentId) => {
    if (!parentId) return [];
    const parent = categoryTree.find(c => c.id === parentId);
    return parent?.children || [];
  };
  
  // Export to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterBrand) params.append('brand_id', filterBrand);
      if (filterSubCategory) {
        params.append('category_id', filterSubCategory);
      } else if (filterCategory) {
        params.append('category_id', filterCategory);
      }
      if (filterSupplier) params.append('supplier_id', filterSupplier);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/export/csv?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: 'Success', description: 'Products exported successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export products', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilterBrand('');
    setFilterCategory('');
    setFilterSubCategory('');
    setFilterSupplier('');
    setSearch('');
    setPage(1);
  };
  
  // Check if any filters are active
  const hasActiveFilters = filterBrand || filterCategory || filterSubCategory || filterSupplier;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {resyncProgress && (
        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3" data-testid="resync-progress">
          <div className="flex justify-between text-xs text-indigo-800 mb-1">
            <span>{resyncing ? 'Syncing prices to POS / all stores — 50 products per batch' : 'Price sync complete'}</span>
            <span data-testid="resync-progress-text">{resyncProgress.processed}/{resyncProgress.total || '…'} products · {resyncProgress.updated} updated</span>
          </div>
          <div className="h-2 rounded-full bg-indigo-100 overflow-hidden"><div className="h-full bg-indigo-600 transition-all" style={{ width: `${resyncProgress.total ? Math.round(resyncProgress.processed / resyncProgress.total * 100) : 5}%` }} /></div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-500">Manage your product catalog</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runResync}
            variant="outline"
            className="border-gray-300"
            disabled={resyncing}
            data-testid="resync-prices-btn"
          >
            {resyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {resyncing && resyncProgress?.total ? `Syncing ${resyncProgress.processed}/${resyncProgress.total}…` : 'Re-sync Prices'}
          </Button>
          <Button 
            onClick={handleExportCSV} 
            variant="outline" 
            className="border-gray-300"
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button onClick={() => navigate('/hq/products/new')} className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-200" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by name, SKU or barcode (any order)..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 bg-white border-gray-300 text-gray-800 focus:border-orange-500" data-testid="search-input" />
            {searchInput && (
              <button type="button" onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="search-clear-btn">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={`border-gray-300 ${hasActiveFilters ? 'bg-orange-50 border-orange-300 text-orange-700' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && <span className="ml-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{[filterBrand, filterCategory, filterSubCategory, filterSupplier].filter(Boolean).length}</span>}
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="filter-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Brand Filter */}
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Brand</Label>
                <Select value={filterBrand || "all"} onValueChange={(v) => { setFilterBrand(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Category Filter */}
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Category</Label>
                <Select value={filterCategory || "all"} onValueChange={(v) => { 
                  setFilterCategory(v === "all" ? "" : v); 
                  setFilterSubCategory('');
                  setPage(1); 
                }}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categoryTree.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sub-Category Filter */}
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Sub-Category</Label>
                <Select 
                  value={filterSubCategory || "all"} 
                  onValueChange={(v) => { setFilterSubCategory(v === "all" ? "" : v); setPage(1); }}
                  disabled={!filterCategory}
                >
                  <SelectTrigger className={`bg-gray-50 border-gray-300 ${!filterCategory ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder="All Sub-Categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All Sub-Categories</SelectItem>
                    {getFilterSubCategories(filterCategory).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Supplier Filter */}
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Supplier</Label>
                <Select value={filterSupplier || "all"} onValueChange={(v) => { setFilterSupplier(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All Suppliers</SelectItem>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Filters applied: {[
                    filterBrand && brands.find(b => b.id === filterBrand)?.name,
                    filterCategory && categoryTree.find(c => c.id === filterCategory)?.name,
                    filterSubCategory && getFilterSubCategories(filterCategory).find(c => c.id === filterSubCategory)?.name,
                    filterSupplier && vendors.find(v => v.id === filterSupplier)?.name
                  ].filter(Boolean).join(', ')}
                </p>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1" /> Clear Filters
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {search && !loading && (
        <p className="mb-3 text-sm text-gray-500" data-testid="search-match-count">
          {totalMatches} {totalMatches === 1 ? 'match' : 'matches'} for
          <span className="font-medium text-gray-700"> &quot;{search}&quot;</span>
          {totalMatches > products.length && <span> · showing page {page} of {totalPages}</span>}
        </p>
      )}

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {search ? `No product matches ${'"'}${search}${'"'}` : 'No products found'}
              </p>
              {search ? (
                <Button onClick={() => setSearchInput('')} variant="link" className="text-orange-600 mt-2" data-testid="clear-search-link">
                  Clear search
                </Button>
              ) : (
                <Button onClick={() => handleOpenDialog()} variant="link" className="text-orange-600 mt-2">
                  Add your first product
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 hover:bg-transparent">
                    <TableHead className="text-gray-600">Product</TableHead>
                    <TableHead className="text-gray-600">SKU / Barcode</TableHead>
                    <TableHead className="text-gray-600">Category</TableHead>
                    <TableHead className="text-gray-600">UOM</TableHead>
                    <TableHead className="text-gray-600">Pricing Status</TableHead>
                    <TableHead className="text-gray-600">MRP</TableHead>
                    <TableHead className="text-gray-600">Cost</TableHead>
                    <TableHead className="text-gray-600">Tax</TableHead>
                    <TableHead className="text-gray-600">Selling Price</TableHead>
                    <TableHead className="text-gray-600">Status</TableHead>
                    <TableHead className="text-gray-600 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map(product => {
                    const priceConfigured = isPricingConfigured(product);
                    const price = product.current_price || {};
                    const defaultSlab = getDefaultSlab(product);
                    const hasUomConversion = product.purchase_uom && product.uom_conversion_factor > 1;
                    
                    return (
                      <TableRow key={product.id} className="border-gray-200 hover:bg-gray-50" data-testid={`product-row-${product.sku}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                              {(product.thumbnail || product.primary_image) ? (
                                <img 
                                  src={(() => {
                                    const imgUrl = product.thumbnail || product.primary_image;
                                    if (imgUrl && !imgUrl.startsWith('http')) return `https://${imgUrl}`;
                                    return imgUrl;
                                  })()} 
                                  alt={product.name} 
                                  className="w-full h-full object-cover rounded-lg"
                                  crossOrigin="anonymous"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full items-center justify-center ${(product.thumbnail || product.primary_image) ? 'hidden' : 'flex'}`}>
                                <Package className="w-5 h-5 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 max-w-[280px] truncate" title={product.name}>
                                <HighlightText text={product.name} query={search} />
                              </p>
                              {product.brand_name && <p className="text-xs text-gray-500">{product.brand_name}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-gray-700 font-mono text-sm">
                              <HighlightText text={product.sku} query={search} />
                            </p>
                            {product.barcodes?.[0] && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Barcode className="w-3 h-3" />
                                <HighlightText text={product.barcodes[0].barcode} query={search} />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-700 text-sm">{product.category_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {priceConfigured ? (
                              <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Completed
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Pending
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              onClick={() => openPricingPage(product.id)}
                              title="Configure Pricing"
                              data-testid={`configure-pricing-btn-${product.id}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                        {/* UOM Column - New */}
                        <TableCell>
                          <div className="text-xs">
                            <div className="text-gray-700">{product.base_uom || 'PCS'}</div>
                            {hasUomConversion && (
                              <div className="text-blue-600 mt-0.5">
                                1 {product.purchase_uom} = {product.uom_conversion_factor} {product.base_uom || 'PCS'}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {priceConfigured ? (
                            <span className="text-gray-700 font-medium">₹{price.mrp?.toFixed(0)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {priceConfigured ? (
                            <div className="text-xs">
                              <span className="text-gray-600">₹{(price.basic_cost || price.cost_price)?.toFixed(0)}</span>
                              {hasUomConversion && (
                                <div className="text-blue-600 mt-0.5">
                                  /{product.purchase_uom}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-gray-300 text-gray-600">{product.tax_rate || 0}%</Badge>
                        </TableCell>
                        <TableCell>
                          {priceConfigured ? (
                            <div>
                              <span className="text-emerald-600 font-semibold">₹{price.selling_price?.toFixed(0)}</span>
                              <span className="text-gray-500 text-xs ml-1">/{product.base_uom || 'PCS'}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            <Badge className={product.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                              {product.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <div className="flex items-center gap-1.5" title="Toggle E-commerce visibility">
                              <Switch
                                checked={!!product.ecom_enabled}
                                disabled={!product.is_active}
                                onCheckedChange={(checked) => handleEcomToggle(product, checked)}
                                data-testid={`ecom-toggle-${product.sku}`}
                                className="data-[state=checked]:bg-blue-600"
                              />
                              <span className={`text-[11px] font-medium flex items-center gap-0.5 ${product.ecom_enabled ? 'text-blue-700' : 'text-gray-400'}`}>
                                <Globe className="w-3 h-3" /> Ecom
                              </span>
                            </div>
                            {product.allow_local_purchase && (
                              <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1 w-fit">
                                <Truck className="w-3 h-3" /> Local
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              onClick={() => navigate(`/hq/products/${product.id}/edit`)} title="Edit Product"
                              data-testid={`edit-product-${product.sku}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {product.is_active ? (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => { setSelectedProduct(product); setDeleteDialogOpen(true); }} title="Deactivate Product"
                                data-testid={`deactivate-product-${product.sku}`}>
                                <PowerOff className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleReactivate(product)} title="Reactivate Product"
                                data-testid={`reactivate-product-${product.sku}`}>
                                <Power className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="border-gray-300 text-gray-600">Previous</Button>
          <span className="px-4 py-2 text-gray-600">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="border-gray-300 text-gray-600">Next</Button>
        </div>
      )}

      {/* Create/Edit Dialog - Basic Info Only */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">SKU *</Label>
                <Input value={formData.sku} onChange={(e) => setFormData(p => ({ ...p, sku: e.target.value.toUpperCase() }))}
                  placeholder="e.g., PRD001" className="bg-gray-50 border-gray-300" disabled={!!selectedProduct} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Product Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Product Name" className="bg-gray-50 border-gray-300" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Product description" className="bg-gray-50 border-gray-300" rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Brand</Label>
                <Select value={formData.brand_id || "none"} onValueChange={(v) => setFormData(p => ({ ...p, brand_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300"><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="none">None</SelectItem>
                    {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Category *</Label>
                <div className="space-y-2">
                  {/* Level 1 - Main Category */}
                  <Select 
                    value={selectedCategories[0] || 'none'} 
                    onValueChange={(v) => handleCategorySelect(0, v === 'none' ? null : v)}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Select main category" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="none">-- Select Category --</SelectItem>
                      {categoryTree.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                          {c.children?.length > 0 && <span className="text-gray-400 text-xs ml-2">({c.children.length} sub)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Level 2 - Sub Category */}
                  {selectedCategories[0] && getSubCategories(selectedCategories[0]).length > 0 && (
                    <Select 
                      value={selectedCategories[1] || 'none'} 
                      onValueChange={(v) => handleCategorySelect(1, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="bg-gray-50 border-gray-300">
                        <SelectValue placeholder="Select sub-category (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="none">-- None --</SelectItem>
                        {getSubCategories(selectedCategories[0]).map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                            {c.children?.length > 0 && <span className="text-gray-400 text-xs ml-2">({c.children.length} sub)</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Level 3 - Sub-sub Category */}
                  {selectedCategories[1] && getSubCategories(selectedCategories[1]).length > 0 && (
                    <Select 
                      value={selectedCategories[2] || 'none'} 
                      onValueChange={(v) => handleCategorySelect(2, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="bg-gray-50 border-gray-300">
                        <SelectValue placeholder="Select sub-sub-category (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="none">-- None --</SelectItem>
                        {getSubCategories(selectedCategories[1]).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Show selected path */}
                  {formData.category_id && (
                    <p className="text-xs text-gray-500">
                      Selected: {categories.find(c => c.id === formData.category_id)?.path || categories.find(c => c.id === formData.category_id)?.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">HSN Code</Label>
                <Input value={formData.hsn_code} onChange={(e) => setFormData(p => ({ ...p, hsn_code: e.target.value }))}
                  placeholder="e.g., 8471" className="bg-gray-50 border-gray-300" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Sales Unit (UOM) *</Label>
                <Select value={formData.uom_id} onValueChange={(v) => {
                  const uom = uoms.find(u => u.id === v);
                  setFormData(p => ({ ...p, uom_id: v, base_uom: uom?.code || '' }));
                }}>
                  <SelectTrigger className="bg-gray-50 border-gray-300"><SelectValue placeholder="Select UOM" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {uoms.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.symbol})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Tax Group *</Label>
                <Select value={formData.tax_group_id} onValueChange={(v) => setFormData(p => ({ ...p, tax_group_id: v }))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300"><SelectValue placeholder="Select tax" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {taxGroups.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.total_rate}%)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* UOM Conversion for Purchase */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-blue-600" />
                <Label className="text-sm font-medium text-blue-800">Purchase Unit Conversion (Optional)</Label>
              </div>
              <p className="text-xs text-blue-600 mb-3">
                If you buy in bulk (cartons/bags) but sell in smaller units (pieces/kg), configure the conversion here.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">Purchase Unit</Label>
                  <Input 
                    value={formData.purchase_uom} 
                    onChange={(e) => setFormData(p => ({ ...p, purchase_uom: e.target.value.toUpperCase() }))}
                    placeholder="e.g., CTN, BAG, BOX" 
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">Conversion Factor</Label>
                  <Input 
                    type="number" 
                    min="1"
                    value={formData.uom_conversion_factor} 
                    onChange={(e) => setFormData(p => ({ ...p, uom_conversion_factor: parseFloat(e.target.value) || 1 }))}
                    placeholder="e.g., 100" 
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">Result</Label>
                  <div className="h-10 flex items-center px-3 bg-gray-100 rounded-md text-sm text-gray-600">
                    1 {formData.purchase_uom || 'Purchase Unit'} = {formData.uom_conversion_factor} {formData.base_uom || 'Sales Unit'}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-700">Barcodes *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addBarcode} className="border-gray-300 text-gray-600">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.barcodes.map((bc, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input value={bc.barcode} onChange={(e) => updateBarcode(idx, e.target.value)}
                      placeholder="Enter barcode" className="bg-gray-50 border-gray-300" />
                    {formData.barcodes.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeBarcode(idx)}
                        className="text-red-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Supplier Mapping Section */}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Suppliers
              </Label>
              <div className="flex gap-2">
                <Select value={selectedSupplierToAdd} onValueChange={setSelectedSupplierToAdd}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 flex-1"><SelectValue placeholder="Select supplier to add" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 z-[9999]">
                    {vendors.filter(v => !formData.supplier_ids.includes(v.id)).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name} ({v.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    if (selectedSupplierToAdd && !formData.supplier_ids.includes(selectedSupplierToAdd)) {
                      const newSupplierIds = [...formData.supplier_ids, selectedSupplierToAdd];
                      // Auto-set primary if first supplier
                      const primaryId = formData.primary_supplier_id || (newSupplierIds.length === 1 ? selectedSupplierToAdd : formData.primary_supplier_id);
                      setFormData(p => ({ ...p, supplier_ids: newSupplierIds, primary_supplier_id: primaryId }));
                      setSelectedSupplierToAdd('');
                    }
                  }}
                  disabled={!selectedSupplierToAdd}
                  className="border-gray-300"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {/* Supplier List with Primary Selection */}
              {formData.supplier_ids.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.supplier_ids.map(supplierId => {
                    const vendor = vendors.find(v => v.id === supplierId);
                    const isPrimary = formData.primary_supplier_id === supplierId;
                    return vendor ? (
                      <div key={supplierId} className={`flex items-center justify-between p-2 rounded border ${isPrimary ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, primary_supplier_id: supplierId }))}
                            className={`p-1 rounded ${isPrimary ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                            title={isPrimary ? 'Primary Supplier' : 'Set as Primary'}
                          >
                            <Star className={`w-4 h-4 ${isPrimary ? 'fill-current' : ''}`} />
                          </button>
                          <span className="text-gray-700 text-sm">
                            {vendor.name} <span className="text-gray-500">({vendor.code})</span>
                          </span>
                          {isPrimary && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Primary</span>
                          )}
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-red-500 hover:text-red-600"
                          onClick={() => {
                            const newIds = formData.supplier_ids.filter(id => id !== supplierId);
                            const newPrimary = formData.primary_supplier_id === supplierId 
                              ? (newIds.length > 0 ? newIds[0] : '') 
                              : formData.primary_supplier_id;
                            setFormData(p => ({ ...p, supplier_ids: newIds, primary_supplier_id: newPrimary }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
              {formData.supplier_ids.length === 0 && (
                <p className="text-xs text-gray-500">No suppliers mapped. Select suppliers who provide this product.</p>
              )}
              {formData.supplier_ids.length > 0 && !formData.primary_supplier_id && (
                <p className="text-xs text-amber-600">⚠️ Please select a primary supplier by clicking the star icon.</p>
              )}
            </div>

            {/* Low Stock Alert */}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Low Stock Alert Qty
              </Label>
              <Input 
                type="number" 
                min="0" 
                step="1"
                value={formData.low_stock_alert_qty} 
                onChange={(e) => setFormData(p => ({ ...p, low_stock_alert_qty: parseFloat(e.target.value) || 0 }))}
                placeholder="Alert when total stock falls below this qty" 
                className="bg-gray-50 border-gray-300" 
              />
              <p className="text-xs text-gray-500">System will alert when combined stock (all stores + warehouses) is below this quantity.</p>
            </div>

            {/* E-Commerce Settings Section */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-blue-200">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-800">E-Commerce Settings</h3>
              </div>
              
              {/* E-commerce Enable Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-500" /> Show on E-Commerce Website
                  </Label>
                  <p className="text-xs text-gray-500">Allow this product to be displayed and sold online</p>
                </div>
                <Switch 
                  checked={formData.ecom_enabled}
                  onCheckedChange={(checked) => setFormData(p => ({ ...p, ecom_enabled: checked }))}
                />
              </div>

              {/* Online Stock Percentage - Only show when ecom_enabled */}
              {formData.ecom_enabled && (
                <div className="space-y-2 pt-2">
                  <Label className="text-gray-700">Online Stock Percentage</Label>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number" 
                      min="0" 
                      max="100"
                      step="5"
                      value={formData.online_stock_percent} 
                      onChange={(e) => {
                        let val = parseInt(e.target.value) || 0;
                        if (val > 100) val = 100;
                        if (val < 0) val = 0;
                        setFormData(p => ({ ...p, online_stock_percent: val }));
                      }}
                      className="bg-white border-gray-300 w-24" 
                    />
                    <span className="text-gray-600">%</span>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300" 
                          style={{ width: `${formData.online_stock_percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Percentage of store stock available for online orders. E.g., 80% means if store has 100 units, 80 will be shown online.
                  </p>
                </div>
              )}

              {/* Shipping Dimensions - Only show when ecom_enabled */}
              {formData.ecom_enabled && (
                <div className="space-y-2 pt-2">
                  <Label className="text-gray-700">Shipping Details</Label>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Weight (g)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="10"
                        value={formData.weight_grams} 
                        onChange={(e) => setFormData(p => ({ ...p, weight_grams: parseFloat(e.target.value) || 0 }))}
                        className="bg-white border-gray-300" 
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Length (cm)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="1"
                        value={formData.length_cm} 
                        onChange={(e) => setFormData(p => ({ ...p, length_cm: parseFloat(e.target.value) || 0 }))}
                        className="bg-white border-gray-300" 
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Width (cm)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="1"
                        value={formData.width_cm} 
                        onChange={(e) => setFormData(p => ({ ...p, width_cm: parseFloat(e.target.value) || 0 }))}
                        className="bg-white border-gray-300" 
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Height (cm)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="1"
                        value={formData.height_cm} 
                        onChange={(e) => setFormData(p => ({ ...p, height_cm: parseFloat(e.target.value) || 0 }))}
                        className="bg-white border-gray-300" 
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Weight is required for shipping cost calculation. Dimensions are optional.</p>
                  <WeightHint grams={formData.weight_grams} length={formData.length_cm} width={formData.width_cm} height={formData.height_cm} />
                </div>
              )}
            </div>

            {/* Local Purchase Settings */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-amber-200">
                <Truck className="w-5 h-5 text-amber-600" />
                <h3 className="font-medium text-amber-800">Local Purchase Settings</h3>
              </div>
              
              {/* Allow Local Purchase Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-amber-500" /> Allow Local Purchase
                  </Label>
                  <p className="text-xs text-gray-500">Enable franchise stores to purchase this product from their local vendors</p>
                </div>
                <Switch 
                  checked={formData.allow_local_purchase}
                  onCheckedChange={(checked) => setFormData(p => ({ ...p, allow_local_purchase: checked }))}
                />
              </div>
            </div>

            {/* Product Images Section - Only show for existing products */}
            {selectedProduct && (
              <div className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-purple-200">
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-purple-600" />
                    <h3 className="font-medium text-purple-800">Product Images</h3>
                  </div>
                  {!storageConfigured && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Storage not configured
                    </Badge>
                  )}
                </div>

                {/* Primary Image */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Primary Image</Label>
                  <div className="flex items-start gap-4">
                    {formData.primary_image ? (
                      <div className="relative group">
                        <img 
                          src={formData.primary_image?.startsWith('http') ? formData.primary_image : `https://${formData.primary_image}`} 
                          alt="Primary" 
                          className="w-32 h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            console.error('Image load error:', formData.primary_image);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiIvPjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ii8+PHBvbHlsaW5lIHBvaW50cz0iMjEgMTUgMTYgMTAgNSAyMSIvPjwvc3ZnPg==';
                          }}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteImage(formData.primary_image, true)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                        <Badge className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs">Primary</Badge>
                      </div>
                    ) : (
                      <div 
                        className={`w-32 h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${storageConfigured ? 'border-purple-300 hover:border-purple-500 hover:bg-purple-50' : 'border-gray-300 bg-gray-50 cursor-not-allowed'}`}
                        onClick={() => storageConfigured && fileInputRef.current?.click()}
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-purple-400 mb-1" />
                            <span className="text-xs text-purple-600">Upload</span>
                          </>
                        )}
                      </div>
                    )}
                    <input 
                      ref={fileInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, true);
                        e.target.value = '';
                      }}
                    />
                    <div className="flex-1 text-sm text-gray-500">
                      <p>The main product image shown in listings.</p>
                      <p className="text-xs mt-1">Max 3MB. JPG, JPEG, PNG, WEBP supported.</p>
                    </div>
                  </div>
                </div>

                {/* Additional Images */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Additional Images</Label>
                  <div className="flex flex-wrap gap-3">
                    {(formData.additional_images || []).map((imgUrl, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={imgUrl?.startsWith('http') ? imgUrl : `https://${imgUrl}`} 
                          alt={`Additional ${idx + 1}`} 
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            console.error('Additional image load error:', imgUrl);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiIvPjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ii8+PHBvbHlsaW5lIHBvaW50cz0iMjEgMTUgMTYgMTAgNSAyMSIvPjwvc3ZnPg==';
                          }}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteImage(imgUrl, false)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* Add more images button */}
                    <div 
                      className={`w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${storageConfigured ? 'border-purple-300 hover:border-purple-500 hover:bg-purple-50' : 'border-gray-300 bg-gray-50 cursor-not-allowed'}`}
                      onClick={() => storageConfigured && additionalFileInputRef.current?.click()}
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-5 h-5 text-purple-400" />
                          <span className="text-xs text-purple-600">Add</span>
                        </>
                      )}
                    </div>
                    <input 
                      ref={additionalFileInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, false);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">Add more images for product gallery (shown on product detail page)</p>
                </div>

                {!storageConfigured && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-700">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Image upload requires asset storage configuration. Go to <strong>Settings → Asset Storage</strong> to configure Bunny CDN.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Show message for new products */}
            {!selectedProduct && (
              <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
                <div className="flex items-center gap-2 text-purple-700">
                  <Image className="w-4 h-4" />
                  <p className="text-sm">
                    <strong>Product Images:</strong> Save the product first, then edit it to upload images.
                  </p>
                </div>
              </div>
            )}

            {/* Info about pricing */}
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> After creating the product, configure pricing (MRP, Cost, Markup/Markdown) 
                in the <strong>Pricing</strong> tab of the product.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? 'Saving...' : 'Save Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Product?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate &quot;{selectedProduct?.name}&quot; and remove it from sales.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
